import type { tInspectionFormInput } from '../types';

export type ExtraUnitInfoEntry = { label?: string; value?: string };

function normalizeKey(value: string): string {
    return value.trim().toLowerCase();
}

export function parseExtraUnitInfo(raw: string | null | undefined): ExtraUnitInfoEntry[] {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

export const UNIT_FUEL_TYPE_LABEL = 'Fuel Type';
export const UNIT_FUEL_TYPE_FIELD_KEY = 'fuelType';

/** Stored in extra unit info so notes save without a new unit column. Hidden from consumable lines. */
export const UNIT_NOTES_EXTRA_LABEL = 'Lifecycle Notes';

export function getUnitFuelType(extraUnitInfo: string | null | undefined): string {
    const extras = parseExtraUnitInfo(extraUnitInfo);
    const match = extras.find((entry) => extraMatchesPrompt(entry.label || '', 'Fuel'));
    return match?.value?.trim() || '';
}

export function upsertUnitFuelType(
    extraUnitInfo: string | null | undefined,
    fuelType: string,
): string | null {
    const extras = parseExtraUnitInfo(extraUnitInfo).filter(
        (entry) => !extraMatchesPrompt(entry.label || '', 'Fuel'),
    );
    const trimmed = fuelType.trim();
    if (trimmed) {
        extras.push({ label: UNIT_FUEL_TYPE_LABEL, value: trimmed });
    }
    return extras.length ? JSON.stringify(extras) : null;
}

function isFuelLabel(label: string): boolean {
    const key = normalizeKey(label);
    return key === 'fuel' || key === 'fuel type' || key.startsWith('fuel ');
}

function extraMatchesPrompt(entryLabel: string, promptLabel: string): boolean {
    const extraKey = normalizeKey(entryLabel);
    const promptKey = normalizeKey(promptLabel);
    if (!extraKey || !promptKey) return false;
    if (extraKey === promptKey) return true;
    if (extraKey === `${promptKey} type`) return true;
    return isFuelLabel(entryLabel) && isFuelLabel(promptLabel);
}

/**
 * Applies per-unit Extra Info onto matching Daily Service consumable lines.
 * Example: Extra Info "Fuel" / "Diesel" (or "Fuel Type" / "Diesel") shows on the Fuel step.
 */
export function applyUnitConsumableDescriptions(
    prompts: tInspectionFormInput[],
    extraUnitInfo: string | null | undefined,
): tInspectionFormInput[] {
    const extras = parseExtraUnitInfo(extraUnitInfo).filter(
        (entry) =>
            entry.label !== UNIT_NOTES_EXTRA_LABEL &&
            !isFuelLabel(entry.label || '') &&
            Boolean(entry.label?.trim()) &&
            Boolean(entry.value?.trim()),
    );
    if (!extras.length) return prompts;

    return prompts.map((prompt) => {
        const label = prompt.label?.trim() || '';
        if (!label) return prompt;
        const match = extras.find((entry) => extraMatchesPrompt(entry.label || '', label));
        const unitText = match?.value?.trim();
        if (!unitText) return prompt;

        const existing = prompt.description?.trim() || '';
        if (!existing) {
            return { ...prompt, description: unitText };
        }
        if (existing === unitText || existing.startsWith(`${unitText}.`) || existing.startsWith(`${unitText} `)) {
            return prompt;
        }
        return { ...prompt, description: `${unitText}. ${existing}` };
    });
}
